type CacheEntry<T> = {
    data?: T;
    expiresAt: number;
    promise?: Promise<T>;
};

const cache = new Map<string, CacheEntry<unknown>>();

interface CacheOptions {
    ttlMs?: number;
}

const DEFAULT_TTL_MS = 5 * 60 * 1000;

export const getCachedResource = async <T>(
    key: string,
    fetcher: () => Promise<T>,
    options: CacheOptions = {}
): Promise<T> => {
    const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
    const now = Date.now();
    const currentEntry = cache.get(key) as CacheEntry<T> | undefined;

    if (currentEntry?.data !== undefined && currentEntry.expiresAt > now) {
        return currentEntry.data;
    }

    if (currentEntry?.promise) {
        return currentEntry.promise;
    }

    const promise = fetcher()
        .then((data) => {
            cache.set(key, {
                data,
                expiresAt: Date.now() + ttlMs,
            });

            return data;
        })
        .catch((error) => {
            cache.delete(key);
            throw error;
        });

    cache.set(key, {
        data: currentEntry?.data,
        expiresAt: currentEntry?.expiresAt ?? 0,
        promise,
    });

    return promise;
};

export const invalidateCachedResource = (key: string) => {
    cache.delete(key);
};

export const invalidateCachedResourceByPrefix = (prefix: string) => {
    Array.from(cache.keys()).forEach((key) => {
        if (key.startsWith(prefix)) {
            cache.delete(key);
        }
    });
};

export const clearRequestCache = () => {
    cache.clear();
};
